import type {
  CheckBadge,
  CheckBatchStatus,
  CheckReport,
  CheckTarget,
} from '@/types/check';
import { checkTargetKey, DEFAULT_ETA_SECONDS_PER_ITEM } from '@/types/check';
import type { LibraryItemKind } from '@/types';

import type { CheckService, CheckStartTarget } from './checkService';
import { isCheckDemoEnabled } from './checkService';

/**
 * fixtureCheckService — 檢查域 in-memory **demo 實作**。
 * -----------------------------------------------------------------------------
 * ⚠️ **不是任何環境的預設實作**:真實路徑一律 `httpCheckService`(R213 起注入真 BE)。
 * 這支只在兩個地方活著 —— 單元測試,以及 demo flag 明確打開
 * (`localStorage['hai.checkDemo']='1'` + reload;`isCheckDemoEnabled()`)時的
 * 樣本展示。介面語意與 http 版**逐條相同**,換實作不改行為(見 `checkService.ts`)。
 *
 * 行為對照 D3 check-progress.js mock:
 *   - 節奏 520ms/子步、4 步/素材(`setInterval(...,520)`;check-progress.js:46)
 *   - 批次第 11 筆(index 10)= 檢查失敗示範(mock `failIdx:10`),不記報告、不計 pass
 *   - 完成一筆寫回一筆;取消保留已完成結果
 * 誠實性(panel 條款):
 *   - **預設全未檢查** — 樣本結果只在使用者按「執行檢查」後由模擬批次寫回;
 *     `getReport` 只對檢查過的 target 回樣本,其餘回 null(= 未檢查)
 *   - 樣本報告疊**真實 item 身分欄**(name / made),不顯示別人的素材名稱
 *   - 計時器活在 module scope — 換頁導航後批次繼續跑(minibar 持續更新);
 *     reload 後 in-memory 遺失,`getBatch` 回 null → store 清 stale(與 http 版
 *     「job 不存在」同語意)
 */

// ─── module-scope demo state ─────────────────────────────────────────────────

interface StoredResult {
  reportKind: 'video' | 'image' | 'text';
  variant: number;
  badge: CheckBadge;
  name?: string;
  made?: string;
  itemId: string;
}

const checkedMap = new Map<string, StoredResult>();
const appliedMap = new Map<string, Record<string, string>>();

interface ActiveBatch {
  status: CheckBatchStatus;
  targets: CheckStartTarget[];
  timer: ReturnType<typeof setInterval> | null;
  subscribers: Set<(s: CheckBatchStatus) => void>;
}

let active: ActiveBatch | null = null;
let batchSerial = 1;
const variantCounter: Record<string, number> = {};

const STEP_MS = 520;
/** mock CHK_BATCH failIdx:10 — 批次第 11 筆示範「檢查失敗」。 */
const DEMO_FAIL_INDEX = 10;

function nowStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 清單 badge 由報告 summary 推導(check-data.js:373-384;fail 次鍵 w、warn 次鍵 u)。 */
export function badgeFromReport(report: Pick<CheckReport, 'summary'>, d: string): CheckBadge {
  const g = { fail: 0, warn: 0, unk: 0 };
  for (const s of report.summary) {
    if (s.k === 'fail') g.fail += s.n;
    else if (s.k === 'warn') g.warn += s.n;
    else if (s.k === 'unk') g.unk += s.n;
  }
  if (g.fail) return { s: 'fail', n: g.fail, w: g.warn, d };
  if (g.warn) return { s: 'warn', n: g.warn, u: g.unk, d };
  if (g.unk) return { s: 'unk', u: g.unk, d };
  return { s: 'pass', d };
}

async function loadReportBase(kind: 'video' | 'image' | 'text', variant: number): Promise<CheckReport> {
  // fixtures dynamic import — ~46KB 樣本字串不進 eager bundle(panel Senior 條款)
  if (kind === 'video') {
    const m = await import('./fixtures/checkReport.video.fixtures');
    return [m.VIDEO_REPORT_SAMPLE, m.VIDEO_REPORT_PASS][variant % 2] as CheckReport;
  }
  if (kind === 'image') {
    const m = await import('./fixtures/checkReport.image.fixtures');
    return [m.IMAGE_REPORT_SAMPLE, m.IMAGE_REPORT_PASS, m.IMAGE_REPORT_WARN, m.IMAGE_REPORT_UNK][
      variant % 4
    ] as CheckReport;
  }
  const m = await import('./fixtures/checkReport.copy.fixtures');
  return m.COPY_REPORT_SAMPLE as CheckReport;
}

function demoKindOf(kind: LibraryItemKind | undefined): 'video' | 'image' | 'text' | null {
  if (kind === 'VIDEO') return 'video';
  if (kind === 'IMAGE') return 'image';
  if (kind === 'TEXT') return 'text';
  return null; // COMPOSE / 未知 → 檢查失敗示範(不猜樣本)
}

/** demo 檢查失敗原因(check-data.js:431 樣本資料)。 */
// eslint-disable-next-line hai/no-hardcoded-cjk -- fixture demo 資料(err reason 是 per-item wire 資料非 UI 模板;等 BE 由報告回)
const DEMO_ERR_REASON = '檔案讀不到，可能已經被刪除或移動。';

function notify() {
  if (!active) return;
  // status 每 tick 新物件;results **只在寫入筆時換 reference**(completeCurrent 以
  // immutable spread 更新)— 未變列的 per-row selector 才能靠 Object.is 擋 re-render。
  const snapshot = { ...active.status };
  for (const cb of active.subscribers) cb(snapshot);
}

function stopTimer() {
  if (active?.timer) {
    clearInterval(active.timer);
    active.timer = null;
  }
}

async function completeCurrent(index: number): Promise<void> {
  if (!active) return;
  const target = active.targets[index];
  if (!target) return;
  const key = checkTargetKey(target);
  const d = nowStamp();
  const demoKind = demoKindOf(target.kind);

  if (index === DEMO_FAIL_INDEX || demoKind === null) {
    active.status.results = { ...active.status.results, [key]: { s: 'err', d, reason: DEMO_ERR_REASON } };
    active.status.failed += 1;
    return;
  }

  const counterKey = demoKind;
  const variant = variantCounter[counterKey] ?? 0;
  variantCounter[counterKey] = variant + 1;
  const base = await loadReportBase(demoKind, variant);
  const badge = badgeFromReport(base, d);
  const stored: StoredResult = {
    reportKind: demoKind,
    variant,
    badge,
    itemId: target.itemId,
    ...(target.name ? { name: target.name } : {}),
    ...(target.made ? { made: target.made } : {}),
  };
  checkedMap.set(key, stored);
  active.status.results = { ...active.status.results, [key]: badge };
}

function startRunner() {
  if (!active) return;
  stopTimer();
  active.timer = setInterval(() => {
    void (async () => {
      if (!active || active.status.state !== 'run') {
        stopTimer();
        return;
      }
      const st = active.status;
      st.step = (st.step + 1) as CheckBatchStatus['step'];
      if (st.step > 3) {
        st.step = 0;
        await completeCurrent(st.done);
        st.done += 1;
        if (st.done >= st.total) {
          st.state = st.failed > 0 ? 'fail' : 'done';
          st.currentKey = null;
          stopTimer();
        } else {
          const next = active.targets[st.done];
          st.currentKey = next ? checkTargetKey(next) : null;
        }
      }
      notify();
    })();
  }, STEP_MS);
}

// ─── service impl ────────────────────────────────────────────────────────────

export function createFixtureCheckService(): CheckService {
  return {
    /**
     * `reportId` 參數存在只為與 http 版簽名一致(demo 每個 target 只有一份樣本報告,
     * 指定哪一份沒有意義)—— 刻意忽略,不假裝支援歷史版本。
     */
    async getReport(target: CheckTarget, _reportId?: string): Promise<CheckReport | null> {
      const key = checkTargetKey(target);
      const stored = checkedMap.get(key);
      if (!stored) return null;
      const base = await loadReportBase(stored.reportKind, stored.variant);
      const applied = appliedMap.get(key);
      return {
        ...base,
        id: stored.itemId,
        ...(stored.name ? { name: stored.name } : {}),
        ...(stored.made ? { made: stored.made } : {}),
        checked: stored.badge.d,
        ...(applied && Object.keys(applied).length > 0 ? { appliedTexts: { ...applied } } : {}),
      };
    },

    /** R212 — 回**第一份快照**(與 http 版同語意:POST 回應就是快照)。 */
    async startBatch(targets: CheckStartTarget[]): Promise<CheckBatchStatus> {
      if (!isCheckDemoEnabled()) {
        throw new Error('[checkService] backend not available (demo flag off)');
      }
      if (targets.length === 0) throw new Error('[checkService] empty targets');
      // 單一 active batch(對齊設計 mock;BE 版由 server 排程)— 舊批次先停
      stopTimer();
      const batchId = `demo-batch-${batchSerial++}`;
      const order = targets.map((t) => checkTargetKey(t));
      active = {
        targets,
        subscribers: active?.subscribers ?? new Set(),
        timer: null,
        status: {
          batchId,
          state: 'run',
          total: targets.length,
          done: 0,
          failed: 0,
          cancelled: 0,
          counts: { fail: 0, warn: 0, unk: 0, pass: 0 },
          step: 0,
          currentKey: order[0] ?? null,
          order,
          results: {},
          reportIds: {},
          etaSecondsPerItem: DEFAULT_ETA_SECONDS_PER_ITEM,
          startedAt: Date.now(),
        },
      };
      startRunner();
      notify();
      return { ...active.status };
    },

    async getBatch(batchId: string): Promise<CheckBatchStatus | null> {
      if (!active || active.status.batchId !== batchId) return null;
      return { ...active.status };
    },

    /**
     * demo 只有單一 active batch(對齊設計 mock)→ 還在跑就回那一筆,否則空陣列
     * (同 http 版:沒有進行中的批次是**空陣列**,不是 null)。
     * 換頁後 minibar 的冷啟還原在 demo 模式下因此也走得通;reload 後 module state
     * 消失回空陣列 = 誠實(fixture 本來就沒有跨 session 狀態)。
     */
    async listActiveBatches(limit = 5): Promise<CheckBatchStatus[]> {
      if (limit <= 0) return [];
      if (!active || active.status.state !== 'run') return [];
      return [{ ...active.status }];
    },

    /** R212 — 回最新快照(同 http 版;404/不存在 → null)。 */
    async cancelBatch(batchId: string): Promise<CheckBatchStatus | null> {
      if (!active || active.status.batchId !== batchId) return null; // 冪等
      if (active.status.state === 'run') {
        active.status.state = 'cancelled';
        active.status.currentKey = null;
        stopTimer();
        notify();
      }
      return { ...active.status };
    },

    subscribeBatch(batchId: string, cb: (status: CheckBatchStatus) => void): () => void {
      if (!active || active.status.batchId !== batchId) return () => {};
      const subs = active.subscribers;
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },

    async checkSingle(target: CheckStartTarget): Promise<CheckBatchStatus | null> {
      // fixture 復用批次 runner(total=1);http 版走獨立端點 POST .../check
      return this.startBatch([target]);
    },

    /**
     * R212 — 收**整批 map**(同 http 版 §6 契約);null = 復原。
     * 回更新後的報告,讓呼叫端不必再 getReport(與 http 版同語意)。
     */
    async applyCopy(
      target: CheckTarget,
      applied: Record<string, string | null>,
    ): Promise<CheckReport | null> {
      const key = checkTargetKey(target);
      const cur = { ...(appliedMap.get(key) ?? {}) };
      for (const [rowKey, text] of Object.entries(applied)) {
        if (text === null) delete cur[rowKey];
        else cur[rowKey] = text;
      }
      appliedMap.set(key, cur);
      return this.getReport(target);
    },

    async recheckGroupItems(target: CheckTarget): Promise<CheckReport | null> {
      // 模擬 BE 輕量重跑延遲;回新報告(fixture 燈號不變,但形狀與 http 版一致)
      await new Promise((r) => setTimeout(r, 600));
      return this.getReport(target);
    },
  };
}

/** 測試用:重置 module-scope demo state(fixture-only;http 版無此需求)。 */
export function __resetFixtureCheckState(): void {
  stopTimer();
  active = null;
  checkedMap.clear();
  appliedMap.clear();
  for (const k of Object.keys(variantCounter)) delete variantCounter[k];
}
